// Regras de publicacao recorrente (logica pura, sem I/O).
//
// Duas formas de regra, sempre no fuso America/Sao_Paulo:
//   - weekly:   dias da semana (0 = domingo ... 6 = sabado) x horarios do dia;
//   - interval: a cada N minutos, ancorado no inicio da recorrencia.
// Inicio (startsAt) e obrigatorio; fim (endsAt) e opcional e inclusivo.
//
// O painel usa nextOccurrence/listOccurrences para mostrar a proxima execucao e
// o calendario; o executor do bot usa planRecurringRun para decidir o que fazer
// com cada ocorrencia vencida (criar o post, adiar pela janela ou pular).
//
// ponytail: offset fixo de -03:00 (Sao Paulo sem horario de verao desde 2019,
// mesma premissa de forwarding/window.ts). Se o fuso voltar a ter DST, trocar
// por conversao via Intl por instante.
import { evaluateWindow, formatHhmm, parseHhmm } from "../forwarding/window.js";
export const RECURRENCE_TIMEZONE = "America/Sao_Paulo";
const SP_OFFSET_MS = -3 * 60 * 60_000;
const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;
/** Menor intervalo aceito (evita rajada nos grupos). Espelhado no SQL. */
export const MIN_INTERVAL_MINUTES = 60;
/** Maior intervalo aceito: 7 dias. Espelhado no SQL. */
export const MAX_INTERVAL_MINUTES = 7 * 24 * 60;
/** Horarios por dia na regra semanal. Espelhado no SQL. */
export const MAX_TIMES_PER_DAY = 12;
/** Ocorrencia vencida ha mais que isso (bot fora do ar) e pulada, sem rajada. */
export const MISSED_GRACE_MS = 15 * MINUTE_MS;
function sortedUnique(values) {
    return [...new Set(values)].sort((a, b) => a - b);
}
function isIntIn(value, min, max) {
    return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}
function normalizeWeekly(r) {
    const rawDays = Array.isArray(r["days"]) ? r["days"] : [];
    const rawTimes = Array.isArray(r["times"]) ? r["times"] : [];
    if (!rawDays.every((d) => isIntIn(d, 0, 6)))
        return null;
    const minutes = rawTimes.map((t) => (typeof t === "string" ? parseHhmm(t) : null));
    if (minutes.some((m) => m === null || m >= 24 * 60))
        return null;
    const days = sortedUnique(rawDays);
    const times = sortedUnique(minutes);
    if (days.length === 0 || times.length === 0 || times.length > MAX_TIMES_PER_DAY)
        return null;
    return { kind: "weekly", days, times };
}
/** Valida o jsonb cru ({kind, days, times} | {kind, everyMinutes}). null = invalido. */
export function normalizeRecurrenceRule(raw) {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw))
        return null;
    const r = raw;
    if (r["kind"] === "weekly")
        return normalizeWeekly(r);
    if (r["kind"] === "interval" && isIntIn(r["everyMinutes"], MIN_INTERVAL_MINUTES, MAX_INTERVAL_MINUTES)) {
        return { kind: "interval", everyMinutes: r["everyMinutes"] };
    }
    return null;
}
/** Formato jsonb persistido (horarios como "HH:MM"). */
export function serializeRecurrenceRule(rule) {
    if (rule.kind === "interval")
        return { kind: "interval", everyMinutes: rule.everyMinutes };
    return { kind: "weekly", days: [...rule.days], times: rule.times.map(formatHhmm) };
}
function nextInterval(everyMinutes, startsAt, after) {
    const anchor = startsAt.getTime();
    const step = everyMinutes * MINUTE_MS;
    if (after.getTime() < anchor)
        return anchor;
    return anchor + (Math.floor((after.getTime() - anchor) / step) + 1) * step;
}
function nextWeekly(rule, lowerMs, inclusive) {
    const local = new Date(lowerMs + SP_OFFSET_MS);
    const dayStartLocal = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate());
    for (let offset = 0; offset <= 7; offset++) {
        if (!rule.days.includes((local.getUTCDay() + offset) % 7))
            continue;
        for (const minute of rule.times) {
            const candidate = dayStartLocal + offset * DAY_MS + minute * MINUTE_MS - SP_OFFSET_MS;
            if (inclusive ? candidate >= lowerMs : candidate > lowerMs)
                return candidate;
        }
    }
    return null;
}
/** Primeira ocorrencia estritamente depois de `after` (e nao antes do inicio). null = acabou. */
export function nextOccurrence(spec, after) {
    const beforeStart = after.getTime() < spec.startsAt.getTime();
    const candidate = spec.rule.kind === "interval"
        ? nextInterval(spec.rule.everyMinutes, spec.startsAt, after)
        : nextWeekly(spec.rule, beforeStart ? spec.startsAt.getTime() : after.getTime(), beforeStart);
    if (candidate === null)
        return null;
    if (spec.endsAt !== null && candidate > spec.endsAt.getTime())
        return null;
    return new Date(candidate);
}
/** Ocorrencias em [from, until], ate `max` itens (calendario e previa). */
export function listOccurrences(spec, from, until, max = 200) {
    const result = [];
    let cursor = nextOccurrence(spec, new Date(from.getTime() - 1));
    while (cursor !== null && cursor.getTime() <= until.getTime() && result.length < max) {
        result.push(cursor);
        cursor = nextOccurrence(spec, cursor);
    }
    return result;
}
/** Decide o destino da ocorrencia vencida `dueAt`, avaliada em `now`. */
export function planRecurringRun(spec, dueAt, now, window) {
    if (spec.endsAt !== null && dueAt.getTime() > spec.endsAt.getTime()) {
        return { action: "skip", occurrenceAt: dueAt, reason: "ended", nextRunAt: null };
    }
    if (now.getTime() - dueAt.getTime() > MISSED_GRACE_MS) {
        return { action: "skip", occurrenceAt: dueAt, reason: "missed", nextRunAt: nextOccurrence(spec, now) };
    }
    const nextRunAt = nextOccurrence(spec, dueAt);
    const evaluation = evaluateWindow(window, dueAt);
    if (evaluation.open) {
        return { action: "create", occurrenceAt: dueAt, scheduledAt: dueAt, nextRunAt };
    }
    if (evaluation.action === "drop") {
        return { action: "skip", occurrenceAt: dueAt, reason: "window_closed", nextRunAt };
    }
    return { action: "create", occurrenceAt: dueAt, scheduledAt: evaluation.releaseAt, nextRunAt };
}
const WEEKDAY_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
function joinPt(items) {
    if (items.length <= 1)
        return items.join("");
    return `${items.slice(0, -1).join(", ")} e ${items[items.length - 1]}`;
}
function describeInterval(everyMinutes) {
    if (everyMinutes % (24 * 60) === 0) {
        const days = everyMinutes / (24 * 60);
        return days === 1 ? "Todo dia" : `A cada ${days} dias`;
    }
    if (everyMinutes % 60 === 0) {
        const hours = everyMinutes / 60;
        return hours === 1 ? "A cada 1 hora" : `A cada ${hours} horas`;
    }
    return `A cada ${everyMinutes} minutos`;
}
/** Texto curto em pt-BR da regra (lista e cards). */
export function describeRecurrence(rule) {
    if (rule.kind === "interval")
        return describeInterval(rule.everyMinutes);
    const times = joinPt(rule.times.map(formatHhmm));
    if (rule.days.length === 7)
        return `Todos os dias às ${times}`;
    const days = joinPt(rule.days.map((d) => WEEKDAY_SHORT[d]));
    return `${days.charAt(0).toUpperCase()}${days.slice(1)} às ${times}`;
}
//# sourceMappingURL=rule.js.map